import { useLayoutEffect, useRef } from 'react';
import { SUMMARY_STATUS } from '@Containers/Summary/constants';

const ENTRY_AUTO_SCROLL_TOP_OFFSET = 12;

function getScrollBehavior() {
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  return prefersReducedMotion ? 'auto' : 'smooth';
}

function runNextFrame(callback) {
  const supportsAnimationFrame = typeof window.requestAnimationFrame === 'function';
  const requestFrame = supportsAnimationFrame ? window.requestAnimationFrame : (frameCallback) => window.setTimeout(frameCallback, 0);
  const cancelFrame = supportsAnimationFrame ? window.cancelAnimationFrame : window.clearTimeout;
  const frameId = requestFrame(callback);

  return () => cancelFrame(frameId);
}

function getScrollContainer(entryElement) {
  if (entryElement?.closest?.('.drawer-panel--modal')) {
    return entryElement.closest('.drawer-panel__inner');
  }

  return entryElement?.closest?.('.drawer-panel__content');
}

function scrollDrawerContentToBottom(entryElement) {
  const scrollContainer = getScrollContainer(entryElement);
  if (!scrollContainer) {
    return;
  }

  if (typeof scrollContainer.scrollTo === 'function') {
    scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: getScrollBehavior() });
  } else {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }
}

function scrollEntryToTop(entryElement) {
  const scrollContainer = getScrollContainer(entryElement);
  if (!scrollContainer || !entryElement?.getBoundingClientRect || !scrollContainer.getBoundingClientRect) {
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const entryRect = entryElement.getBoundingClientRect();
  const targetTop = Math.max(0, scrollContainer.scrollTop + entryRect.top - containerRect.top - ENTRY_AUTO_SCROLL_TOP_OFFSET);

  if (typeof scrollContainer.scrollTo === 'function') {
    scrollContainer.scrollTo({ top: targetTop, behavior: getScrollBehavior() });
  } else {
    scrollContainer.scrollTop = targetTop;
  }
}

function focusElement(element) {
  element?.focus?.({ preventScroll: true });
}

/**
 * Coordinates panel movement and focus for each Assistant entry lifecycle transition.
 *
 * Both operations run in one post-commit frame: the panel moves first, then focus is applied with
 * preventScroll so the browser cannot also scroll the Results page. Leyden's GlobalFocusToggle remains
 * authoritative for whether the focus indicator is visible.
 */
export default function useSummaryLifecycleNavigation({
  enabled,
  attempt,
  latestSummaryId,
  latestEntryRef,
  stopButtonRef,
  regenerateButtonRef,
}) {
  const previousEntryRef = useRef(null);
  const latestEntryId = attempt?.id || latestSummaryId;
  const attemptStatus = attempt?.status;
  const isLoading = attemptStatus === SUMMARY_STATUS.LOADING;
  const hasAttempt = Boolean(attempt);

  useLayoutEffect(() => {
    if (!enabled || !latestEntryId) {
      previousEntryRef.current = null;
      return undefined;
    }

    const previousEntry = previousEntryRef.current;
    const entryAdded = previousEntry?.id !== latestEntryId;
    const loadingStarted = isLoading && (entryAdded || !previousEntry?.isLoading);
    const loadingFinished = !isLoading && previousEntry?.id === latestEntryId && previousEntry?.isLoading;
    const shouldAlignEntry = !isLoading && (entryAdded || loadingFinished);

    previousEntryRef.current = { id: latestEntryId, isLoading };

    if (!loadingStarted && !shouldAlignEntry) {
      return undefined;
    }

    return runNextFrame(() => {
      const latestEntry = latestEntryRef?.current;

      if (loadingStarted) {
        scrollDrawerContentToBottom(latestEntry);
      } else {
        scrollEntryToTop(latestEntry);
      }

      if (loadingStarted) {
        focusElement(stopButtonRef?.current);
      } else if (loadingFinished && attemptStatus === SUMMARY_STATUS.ERROR) {
        focusElement(regenerateButtonRef?.current);
      } else if (loadingFinished && !hasAttempt && latestSummaryId === latestEntryId) {
        focusElement(latestEntry);
      }
    });
  }, [attemptStatus, enabled, hasAttempt, isLoading, latestEntryId, latestEntryRef, latestSummaryId, regenerateButtonRef, stopButtonRef]);
}
